import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import dayjs, { ManipulateType } from 'dayjs';
import { isEqual } from 'lodash-es';
import { useRoute } from 'vue-router';
import { stringifyDateString } from '@/common/components/molecules/timeController/utils';
import { parsePeriod } from '@/common/utils/core/time/parse';
import { getTimePeriodByRange } from '@/common/utils/core/time/stringify';
import { getSafeDateTime } from '@/common/utils/core/time/calculate';
import {
  GLOBAL_TIME_RANGE_FORMAT,
  DEFAULT_PERIOD_ITEMS_ALL,
} from '@/common/components/molecules/timeController/constants';
import { useInternational } from '@/common/locale';
import { DEFAULT_PAST_INPUT_TIME, MAX_SEARCH_INPUT_NUMBER_VALUE, TIME_UNIT } from '@/common/utils';
import { getOnlyNumberString } from '@/common/utils/string.utils';
import { webStorageController, calculatePopupPosition } from '@/common/utils/browser';
import type {
  SelectOption,
  TimePeriodData,
  ReadOnlyTimes,
  ValueOf,
  PaTimePeriodFilterOptions,
} from '@/types/common.types';
import { useMessage } from '@/common/inject/useMessage';
import { getPeriodToSeconds, getTzTimeNow, secondsToPeriod } from '@/common/utils/commonUtils';
import type { TimezoneName } from '@/common/utils';

export interface Props {
  modelValue: string;
  periods?: TimePeriodData[];
  fromTime?: string;
  toTime?: string;
  timeUnit?: 'day' | 'sec';
  hideDropdownWrapper?: boolean;
  disabled?: boolean;
  isError?: boolean;
  timeRangeSecLimit?: number;
  minTimeRangeSecLimit?: number;
  useCalendar?: boolean;
  useInput?: boolean;
  readonlyTimes?: ReadOnlyTimes;

  // for PA Filters
  paTimeFilterOptions?: PaTimePeriodFilterOptions;

  calendarPos?: 'left' | 'right';
  /** 시간 계산·표시 기준 타임존 */
  timezone?: TimezoneName;
}

export interface Emit {
  (e: 'update:modelValue', periodValue: string): void;
  (e: 'change-period', periodValue: string | string[]): void;
  (
    e: 'change-period-by-calendar',
    value: {
      fromTime: string;
      toTime: string;
      period: string;
    },
  ): void;
  (e: 'dropdown-open'): void;
}

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();
  const { showErrorMsg } = useMessage();

  const isShowDropdown = ref(false);
  const isShowDatePicker = ref(false);
  const dateTimePeriod = ref<[string, string]>(['', '']);

  const triggerRef = ref<HTMLElement>();
  const popupRef = ref<HTMLElement>();
  const popupStyle = reactive({
    top: 'auto',
    bottom: 'auto',
    left: 'auto',
    right: 'auto',
  });

  const isPopupVisible = computed(() => isShowDropdown.value || isShowDatePicker.value);

  const updatePopupPosition = async () => {
    await nextTick();
    const triggerEl = triggerRef.value;
    const popupEl = popupRef.value;
    if (!triggerEl || !popupEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const popupRect = popupEl.getBoundingClientRect();
    const windowSize = { width: window.innerWidth, height: window.innerHeight };

    const pos = calculatePopupPosition(triggerRect, popupRect, windowSize, {
      gap: 4,
      align: props.calendarPos === 'right' ? 'left' : 'right',
    });

    if (isShowDatePicker.value) {
      pos.top = `${triggerRect.top + triggerRect.height + 4}px`;
      pos.bottom = 'auto';
    }

    Object.assign(popupStyle, pos);
  };

  useEventListener(
    window,
    'scroll',
    (event: Event) => {
      if (!isPopupVisible.value) return;
      if (popupRef.value?.contains(event.target as Node)) return;
      closeAll();
    },
    { capture: true },
  );

  // radix-vue select 트리거는 pointerdown 에서 preventDefault 하여 mousedown/click 호환 이벤트를
  // 억제한다. 그 결과 mousedown 기반 v-click-outside(closeAll)가 발화하지 않아 다른 셀렉트를
  // 열어도 Period 드롭다운이 닫히지 않음 → pointerdown(capture)으로 외부 클릭을 직접 감지해 닫는다.
  useEventListener(
    document,
    'pointerdown',
    (event: Event) => {
      if (!isPopupVisible.value) return;
      const target = event.target as Node;
      // 트리거(토글)·팝업 내부 클릭은 제외 (열기/내부 조작 유지)
      if (triggerRef.value?.contains(target)) return;
      if (popupRef.value?.contains(target)) return;
      closeAll();
    },
    { capture: true },
  );

  const DEFAULT_TIME_UNIT_ITEMS = [
    {
      name: t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_UNIT', {
        unit: t('DESC.UI.MONTH'),
      }),
      value: TIME_UNIT.month,
    },
    {
      name: t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_UNIT', { unit: t('DESC.UI.DAY') }),
      value: TIME_UNIT.day,
    },
    {
      name: t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_UNIT', { unit: t('DESC.UI.HOUR') }),
      value: TIME_UNIT.hour,
    },
    {
      name: t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_UNIT', { unit: t('DESC.UI.MINUTE') }),
      value: TIME_UNIT.minute,
    },
    {
      name: t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_UNIT', { unit: t('DESC.UI.SECOND') }),
      value: TIME_UNIT.second,
    },
  ];

  const TIME_UNIT_SECONDS: Record<ValueOf<typeof TIME_UNIT>, number> = {
    [TIME_UNIT.day]: 60 * 60 * 24,
    [TIME_UNIT.hour]: 60 * 60,
    [TIME_UNIT.minute]: 60,
    [TIME_UNIT.second]: 1,
    // NOTE: 임의로 31일 고정, 사용하는 곳 없음 2026-04-07
    [TIME_UNIT.month]: 60 * 60 * 24 * 31,
  };

  const isAllowedByReadonlyTimes = (unit: ValueOf<typeof TIME_UNIT>) => {
    if (unit === TIME_UNIT.second) {
      return !props.readonlyTimes?.second;
    }
    if (unit === TIME_UNIT.minute) {
      return !props.readonlyTimes?.minute;
    }
    if (unit === TIME_UNIT.hour) {
      return !props.readonlyTimes?.hour;
    }
    return true;
  };

  const getValidInputBounds = (unit: ValueOf<typeof TIME_UNIT>) => {
    const unitSeconds = TIME_UNIT_SECONDS[unit];
    const min = props.minTimeRangeSecLimit
      ? Math.max(1, Math.ceil(props.minTimeRangeSecLimit / unitSeconds))
      : 1;
    const maxByRange = props.timeRangeSecLimit
      ? Math.floor(props.timeRangeSecLimit / unitSeconds)
      : MAX_SEARCH_INPUT_NUMBER_VALUE;
    const max = Math.max(0, Math.min(MAX_SEARCH_INPUT_NUMBER_VALUE, maxByRange));

    return { min, max };
  };

  const timeUnitItems = computed<SelectOption[]>(() =>
    (props.paTimeFilterOptions?.customTimeUnitItems?.length
      ? props.paTimeFilterOptions.customTimeUnitItems
      : DEFAULT_TIME_UNIT_ITEMS
    ).filter(({ value }) => {
      const unit = value as ValueOf<typeof TIME_UNIT>;
      if (!isAllowedByReadonlyTimes(unit)) {
        return false;
      }

      const { min, max } = getValidInputBounds(unit);
      return max >= min;
    }),
  );
  const timeFormat = computed<string>(
    () => props.paTimeFilterOptions?.timeFormat ?? GLOBAL_TIME_RANGE_FORMAT,
  );

  const pastInputTime = ref<string>(
    props.paTimeFilterOptions?.defaultPastInputValue?.value ?? DEFAULT_PAST_INPUT_TIME.value,
  );
  const pastInputUnit = ref<ValueOf<typeof TIME_UNIT>>(
    props.paTimeFilterOptions?.defaultPastInputValue?.unit ?? DEFAULT_PAST_INPUT_TIME.unit,
  );
  const pastInputError = ref<string>('');

  const initPastInputInfo = () => {
    pastInputTime.value =
      props.paTimeFilterOptions?.defaultPastInputValue?.value ?? DEFAULT_PAST_INPUT_TIME.value;
    pastInputUnit.value =
      props.paTimeFilterOptions?.defaultPastInputValue?.unit ?? DEFAULT_PAST_INPUT_TIME.unit;
    pastInputError.value = '';
    if (!timeUnitItems.value.some(({ value }) => value === pastInputUnit.value)) {
      pastInputUnit.value =
        (timeUnitItems.value[0]?.value as ValueOf<typeof TIME_UNIT> | undefined) ??
        DEFAULT_PAST_INPUT_TIME.unit;
    }

    const { min, max } = getValidInputBounds(pastInputUnit.value);
    const normalizedValue = Math.min(Math.max(Number(pastInputTime.value) || min, min), max);
    pastInputTime.value = `${normalizedValue}`;
  };

  const openDatePicker = async () => {
    if (props.fromTime && props.toTime && props.modelValue.includes('custom')) {
      dateTimePeriod.value = [props.fromTime, props.toTime];
    } else {
      const now = props.timezone ? dayjs.tz(undefined, props.timezone) : getTzTimeNow();
      dateTimePeriod.value = [
        now.startOf('day').format(timeFormat.value),
        now.endOf('day').format(timeFormat.value),
      ];
    }

    isShowDatePicker.value = !isShowDatePicker.value;
    await updatePopupPosition();
  };

  const closeDatePicker = async () => {
    isShowDatePicker.value = false;
    if (isShowDropdown.value) {
      await updatePopupPosition();
    }
  };

  const toggleDropdown = async () => {
    if (props.disabled) {
      return;
    }

    if (props.hideDropdownWrapper && props.useCalendar) {
      if (isShowDatePicker.value) {
        closeAll();
        return;
      }
      openDatePicker();
      emit('dropdown-open');
      return;
    }

    isShowDropdown.value = !isShowDropdown.value;
    if (isShowDropdown.value) {
      await updatePopupPosition();
      emit('dropdown-open');
    } else {
      isShowDatePicker.value = false;
    }
  };

  const closeDropdown = () => {
    isShowDropdown.value = false;
  };

  const closeAll = (e?: Event) => {
    // teleport로 팝업이 DOM 트리 밖에 있어 v-click-outside가 팝업 클릭도 'outside'로 감지하므로,
    // 팝업 내부 클릭은 무시하여 의도치 않은 닫힘을 방지
    if (e?.target) {
      const popupEl = popupRef.value;
      if (popupEl?.contains(e.target as Node) && popupEl !== e.target) return;
    }
    closeDatePicker();
    closeDropdown();
  };

  const usePropsPeriods = !!(props?.periods && props.periods.length);
  const periods = computed<TimePeriodData[]>(() => {
    return (usePropsPeriods ? props.periods : DEFAULT_PERIOD_ITEMS_ALL) as TimePeriodData[];
  });

  const quickPeriods = computed<TimePeriodData[]>(() => {
    return periods.value.filter(({ value }) => {
      if (props.timeRangeSecLimit) {
        return parsePeriod(value).offset <= props.timeRangeSecLimit && !value.includes('custom');
      }
      return !value.includes('custom');
    });
  });

  const recentlySearches = ref<
    {
      id: number;
      value: TimePeriodData;
    }[]
  >([]);

  const route = useRoute();
  const storageKey = ref<string>('');

  watch(
    () => route.path,
    () => {
      storageKey.value = `timePeriodV2_recently_searches_${route.path}`;

      recentlySearches.value = JSON.parse(
        webStorageController.getItem({
          type: 'local',
          key: storageKey.value,
        }) ?? '[]',
      );
    },
    {
      immediate: true,
    },
  );

  const MAXIMUM_HISTORY_SIZE = 5;
  const saveRecentlySearch = (param: TimePeriodData) => {
    const duplicatedItemIndex = recentlySearches.value.findIndex(({ value }) =>
      isEqual(value, param),
    );
    if (duplicatedItemIndex > -1) {
      recentlySearches.value.splice(duplicatedItemIndex, 1);
    }

    recentlySearches.value.unshift({
      id: Date.now(),
      value: param,
    });

    if (recentlySearches.value.length > MAXIMUM_HISTORY_SIZE) {
      recentlySearches.value.pop();
    }

    webStorageController.setItem({
      type: 'local',
      key: storageKey.value,
      value: JSON.stringify(recentlySearches.value),
    });
  };

  const deleteRecentlySearch = (index: number) => {
    recentlySearches.value.splice(index, 1);

    webStorageController.setItem({
      type: 'local',
      key: storageKey.value,
      value: JSON.stringify(recentlySearches.value),
    });
  };

  const selectedPeriod = computed({
    get: () => props.modelValue,
    set: (val: string) => {
      if (props.disabled) {
        return;
      }
      emit('update:modelValue', val);
      emit('change-period', val);
    },
  });

  const periodShortName = computed(
    () => periods.value.find(({ value }) => value === selectedPeriod.value)?.shortName,
  );

  const periodName = computed(
    () => periods.value.find(({ value }) => value === selectedPeriod.value)?.name,
  );

  const onClickQuickPeriod = (period: TimePeriodData) => {
    selectedPeriod.value = period.value;
    saveRecentlySearch(period);
    closeAll();
  };

  const onSelectByCalendar = (value: string[]) => {
    const fromTime = dayjs(value[0]).format(timeFormat.value);
    const toTime = dayjs(value[1]).format(timeFormat.value);
    const period = getTimePeriodByRange(value[0], value[1]);

    emit('change-period-by-calendar', {
      fromTime,
      toTime,
      period,
    });

    saveRecentlySearch({
      name: `${fromTime} ~ ${toTime}`,
      value: `${fromTime} ~ ${toTime}`,
      shortName: period,
    });

    closeAll();
  };

  const onClickRecentlySearch = (selectedValue: TimePeriodData) => {
    if (
      props.timeRangeSecLimit &&
      parsePeriod(selectedValue.shortName).offset > props.timeRangeSecLimit
    ) {
      showErrorMsg({
        msgStr: t('MESSAGE.VALIDATION_GUIDE', {
          value: stringifyDateString(props.timeRangeSecLimit),
        }),
      });
      return;
    }

    if (selectedValue.name.includes('~')) {
      const [fromTime, toTime] = selectedValue.name.split(' ~ ');

      emit('change-period-by-calendar', {
        fromTime,
        toTime,
        period: selectedValue.shortName,
      });
    } else {
      selectedPeriod.value = selectedValue.value;
    }

    closeAll();
  };

  const getDisplayName = (inputTime: string, inputUnit: ValueOf<typeof TIME_UNIT>) => {
    const singularUnit = {
      [TIME_UNIT.month]: t('DESC.UI.MONTH'),
      [TIME_UNIT.day]: t('DESC.UI.DAY'),
      [TIME_UNIT.hour]: t('DESC.UI.HOUR'),
      [TIME_UNIT.minute]: t('DESC.UI.MINUTE'),
      [TIME_UNIT.second]: t('DESC.UI.SECOND'),
    };
    const pluralUnit = {
      [TIME_UNIT.month]: t('WORD.PLURAL_ELAPSED_TIME_UNIT', { value: t('DESC.UI.MONTH') }),
      [TIME_UNIT.day]: t('WORD.PLURAL_ELAPSED_TIME_UNIT', { value: t('DESC.UI.DAY') }),
      [TIME_UNIT.hour]: t('WORD.PLURAL_ELAPSED_TIME_UNIT', { value: t('DESC.UI.HOUR') }),
      [TIME_UNIT.minute]: t('WORD.PLURAL_ELAPSED_TIME_UNIT', { value: t('DESC.UI.MINUTE') }),
      [TIME_UNIT.second]: t('WORD.PLURAL_ELAPSED_TIME_UNIT', { value: t('DESC.UI.SECOND') }),
    };

    return t('WORD.TIME_PERIOD_ITEMS.PAST_DYNAMIC_TIME', {
      value: inputTime,
      unit: `${+inputTime > 1 ? pluralUnit[inputUnit] : singularUnit[inputUnit]}`,
    });
  };

  const getCustomPeriod = ({
    inputTime,
    inputUnit,
    diffDates,
  }: {
    inputTime: string;
    inputUnit: ValueOf<typeof TIME_UNIT>;
    diffDates: number;
  }) => {
    const displayName = getDisplayName(inputTime, inputUnit);
    const searchUnit = inputUnit === 'month' ? TIME_UNIT.day : inputUnit;
    const searchTime = inputUnit === 'month' ? diffDates : inputTime;
    return {
      name: displayName,
      value: `p${searchTime}${searchUnit}`,
      shortName: `${searchTime}${inputUnit === 'month' ? TIME_UNIT.day : inputUnit}`,
    };
  };

  const getDiffDates = (
    curTime: string | undefined,
    option: { value: number; unit: ManipulateType },
  ) => {
    if (!curTime) {
      return 0;
    }
    const formattedCurTime = dayjs(curTime, timeFormat.value);
    const prevTime = getSafeDateTime(formattedCurTime.subtract(option.value, option.unit));
    return formattedCurTime.diff(prevTime, 'day');
  };

  const onInputValidation = (_inputTime: string | number) => {
    const inputTime = `${_inputTime}`;

    if (!inputTime) {
      pastInputError.value = t('MESSAGE.ENTER_THE_TIME');
      return;
    }

    const rangeInSec = getPeriodToSeconds(`${inputTime}${pastInputUnit.value}`);

    if (
      props.timeRangeSecLimit &&
      props.timeRangeSecLimit > 0 &&
      rangeInSec > props.timeRangeSecLimit
    ) {
      const period = secondsToPeriod(props.timeRangeSecLimit);
      pastInputError.value = t('MESSAGE.VALIDATION_GUIDE', {
        value: t(`WORD.${period.locale}`, { value: period.value }),
      });
      return;
    }

    if (
      props.minTimeRangeSecLimit &&
      props.minTimeRangeSecLimit > 0 &&
      rangeInSec < props.minTimeRangeSecLimit
    ) {
      const period = secondsToPeriod(props.minTimeRangeSecLimit);
      pastInputError.value = t('MESSAGE.VALIDATION_GUIDE_MIN', {
        value: t(`WORD.${period.locale}`, { value: period.value }),
      });
      return;
    }

    pastInputError.value = '';
    const { min, max } = getValidInputBounds(pastInputUnit.value);
    pastInputTime.value = getOnlyNumberString(inputTime, {
      min,
      max: max || MAX_SEARCH_INPUT_NUMBER_VALUE,
    });
  };

  const onClickInputTimeOk = (inputTime: string, inputUnit: ValueOf<typeof TIME_UNIT>) => {
    const customPeriod = getCustomPeriod({
      inputTime,
      inputUnit,
      diffDates:
        inputUnit === 'month'
          ? getDiffDates(props.toTime, { value: +inputTime, unit: 'month' })
          : -1,
    });
    selectedPeriod.value = `p${inputTime}${inputUnit}`;
    selectedPeriod.value = customPeriod.value;
    saveRecentlySearch(customPeriod);
    closeAll();
  };

  watch(
    () => isShowDropdown.value,
    (isShow) => {
      if (isShow) {
        initPastInputInfo();
      }
    },
  );

  watch(
    () => pastInputUnit.value,
    (unit) => {
      const { min, max } = getValidInputBounds(unit);
      const currentValue = Number(pastInputTime.value) || min;
      const normalizedValue = Math.min(Math.max(currentValue, min), max);
      pastInputTime.value = `${normalizedValue}`;
      onInputValidation(pastInputTime.value);
    },
  );

  return {
    t,

    isShowDropdown,
    toggleDropdown,
    closeAll,

    periodName,
    periodShortName,

    quickPeriods,
    onClickQuickPeriod,

    recentlySearches,
    onClickRecentlySearch,
    deleteRecentlySearch,

    isShowDatePicker,
    dateTimePeriod,
    openDatePicker,
    closeDatePicker,
    onSelectByCalendar,

    triggerRef,
    popupRef,
    popupStyle,
    isPopupVisible,

    timeUnitItems,
    pastInputTime,
    pastInputUnit,
    pastInputError,
    onInputValidation,
    onClickInputTimeOk,
  };
};
